import { t } from './i18n.js';

let pixiPromise;

async function loadPixi() {
  if (!pixiPromise) pixiPromise = import('./pixi-runtime.js');
  return pixiPromise;
}

async function checkGraphics() {
  try {
    const runtime = await loadPixi();
    return await runtime.probeGraphics();
  } catch {
    return false;
  }
}

function announce(message, priority = 'polite') {
  const region = document.getElementById('v2-live-region');
  if (!region) return;
  region.setAttribute('aria-live', priority);
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

function vibrate(pattern = 20) {
  if (localStorage.getItem('lan-games-haptics') === 'off') return false;
  return navigator.vibrate?.(pattern) || false;
}

function createSlots(root) {
  root.classList.add('renderer-v2-host');
  root.innerHTML = `
    <div class="renderer-v2-stage" data-v2-slot="stage"></div>
    <div class="renderer-v2-hud" data-v2-slot="hud"></div>
    <div class="renderer-v2-overlay" data-v2-slot="overlay"></div>`;
  return {
    root,
    stage: root.querySelector('[data-v2-slot="stage"]'),
    hud: root.querySelector('[data-v2-slot="hud"]'),
    overlay: root.querySelector('[data-v2-slot="overlay"]'),
    actions: document.getElementById('action-buttons'),
  };
}

function createContext({ root, state, myUserId, emitAction, options = {} }) {
  return {
    slots: createSlots(root),
    state,
    viewer: { userId: myUserId, isSpectator: Boolean(options.isSpectator) },
    emitAction,
    t,
    announce,
    vibrate,
    loadPixi,
    async requireGraphics() {
      const runtime = await loadPixi();
      const supported = await runtime.probeGraphics();
      if (!supported) {
        const error = new Error(t('graphics.unsupportedBody'));
        error.code = 'GRAPHICS_UNAVAILABLE';
        throw error;
      }
      return runtime;
    },
  };
}

export function adaptRendererV2(definition) {
  let instance = null;
  let context = null;
  return {
    rendererInterfaceVersion: '2.0',
    async init(root, state, myUserId, emitAction, options = {}) {
      context = createContext({ root, state, myUserId, emitAction, options });
      instance = await definition.create(context);
      await instance?.mount?.();
    },
    update(state) {
      if (context) context.state = state;
      return instance?.update?.(state);
    },
    onEvent(event, state) {
      return instance?.onEvent?.(event, state);
    },
    getPlayerCardData(player, state) {
      return instance?.getPlayerCardData?.(player, state) || {};
    },
    destroy() {
      try {
        instance?.destroy?.();
      } finally {
        context?.slots?.root?.replaceChildren();
        instance = null;
        context = null;
      }
    },
  };
}

export function enhanceLegacyRenderer(renderer) {
  let context = null;
  return {
    rendererInterfaceVersion: '2.0',
    async init(root, state, myUserId, emitAction, options = {}) {
      context = createContext({ root, state, myUserId, emitAction, options });
      await renderer.init(context.slots.stage, state, myUserId, emitAction, options);
    },
    update(state) {
      if (context) context.state = state;
      return renderer.update?.(state);
    },
    onEvent(event, state) {
      return renderer.onEvent?.(event, state);
    },
    getPlayerCardData(player, state) {
      return renderer.getPlayerCardData?.(player, state) || {};
    },
    destroy() {
      try {
        renderer.destroy?.();
      } finally {
        context?.slots?.root?.replaceChildren();
        context = null;
      }
    },
  };
}

export const RendererV2 = {
  adapt: adaptRendererV2,
  enhanceLegacy: enhanceLegacyRenderer,
  loadPixi,
  checkGraphics,
  announce,
  vibrate,
};
